// React Dependencies
import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Typography, Button, IconButton, Tooltip } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';

// Internal Dependencies
import ApiService from '../../../services.js/apiService';
import OverLoading from '../../global/OverLoading';
import OutletsManagementDialog from './OutletsManagementDialog';
import { updateModalContent as updateModalAction, toggleModal as toggleModalAction } from '../../../actions/modal';
import { IsEmpty } from '../../../common/checks';
import { Colours, Status } from '../../../common/constants';

const ParticipateOutlet = ({ match, updateModalContent, toggleModal }) => {
  const { pageId, loyaltyCardId } = match.params;

  const [participateOutlets, setParticipateOutlets] = useState([]);
  const [isOutletsManagementModalOpen, setIsOutletsManagementModalOpen] = useState(false);
  const [status, setStatus] = useState(Status.Draft);
  const [isLoading, setIsLoading] = useState(true);

  const isDeactivated = status === Status.Deactivated;

  const modifyParticipateOutlets = (outlets) => {
    setParticipateOutlets(outlets.map((o) => ({
      ...o, fullAddress: `${o.address} ${!IsEmpty(o.unitNo) ? `#${o.unitNo}` : ''} Singapore ${o.postalCode}`,
    })));
  };

  const getLoyaltyOutlets = async () => {
    try {
      const data = await ApiService.getParticipateOutlets(pageId, loyaltyCardId);
      modifyParticipateOutlets(data.rows);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getLoyaltyCard = async () => {
    try {
      setIsLoading(true);
      const loyaltyCard = await ApiService.getLoyaltyCard(pageId, loyaltyCardId);
      setStatus(loyaltyCard.status);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteModal = (outletId) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'This outlet will not be participating in this loyalty card anymore.',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setIsLoading(true);
            await ApiService.deleteLoyaltyCardParticipateOutlet({ pageId, loyaltyCardId, outletId });

            getLoyaltyOutlets();
            toast.success('Successfully removed participating outlet.');
          } catch (err) {
            toast.error(err.message);
          } finally {
            setIsLoading(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const columns = useMemo(() => [
    {
      title:     'Outlet ID',
      field:     'id',
      filtering: true,
      editable:  'never',
      cellStyle: { width: '10%' },
    },
    {
      title:     'Brand',
      field:     'brand',
      filtering: true,
      editable:  'never',
      cellStyle: { width: '20%' },
    },
    {
      title:     'Address',
      field:     'fullAddress',
      filtering: true,
      editable:  'never',
      cellStyle: { width: '40%' },
    },
    {
      title:     'Tel',
      field:     'tel',
      filtering: true,
      editable:  'never',
      cellStyle: { width: '20%' },
    },
    {
      title:     'Action',
      filtering: false,
      editable:  'never',
      cellStyle: { width: '10%' },
      render:    (row) => (
        !isDeactivated && (
          <Tooltip title="Delete">
            <IconButton size="medium" onClick={() => handleDeleteModal(row.id)}>
              <DeleteIcon htmlColor={Colours.Black} />
            </IconButton>
          </Tooltip>
        )
      ),
    },
  ], [isDeactivated]);

  useEffect(() => {
    getLoyaltyOutlets();
    getLoyaltyCard();
  }, []);

  return (
    <div className="container-fluid">
      {isLoading && <OverLoading />}
      <div className="" style={{ backgroundColor: Colours.White }}>
        <div className="row mb-3">
          <div className="col-12 pl-0 d-flex justify-content-between align-items-end">
            <div>
              <Typography
                style={{
                  fontSize: 12,
                  color:    '#7C7C7C',
                }}
                className="mb-2"
              >
                Loyalty Card ID:
                {' '}
                {match.params.loyaltyCardId}
              </Typography>
              <Typography style={{ fontSize: 16 }} className="font-weight-bold" color={Colours.Gray}>Participating Outlets</Typography>
              <Typography style={{ fontSize: 13 }}>The following outlets are participating in this loyalty card</Typography>
            </div>
            {!isDeactivated && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsOutletsManagementModalOpen(true)}
              >
                Add / Remove
              </Button>
            )}
          </div>
        </div>

        <div className="row pb-4">
          <div className="col-12 pl-0">
            <MaterialTable
              title=""
              isLoading={isLoading}
              columns={columns}
              data={participateOutlets}
              components={{ Toolbar: () => null }}
              options={{ filtering: true }}
            />
          </div>
        </div>
      </div>

      {
        isOutletsManagementModalOpen && (
          <OutletsManagementDialog
            open={isOutletsManagementModalOpen}
            onClose={() => setIsOutletsManagementModalOpen(false)}
            pageId={pageId}
            loyaltyCardId={loyaltyCardId}
            onRefresh={(outlets) => modifyParticipateOutlets(outlets)}
          />
        )
      }
    </div>
  );
};

ParticipateOutlet.propTypes = {
  match:              PropTypes.shape(Object).isRequired,
  toggleModal:        PropTypes.func.isRequired,
  updateModalContent: PropTypes.func.isRequired,
};

export default connect(null, { toggleModal: toggleModalAction, updateModalContent: updateModalAction })(ParticipateOutlet);
